import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { useCallback, useMemo } from "react";
import { createS3Client } from "./s3-client.factory";

export const isAbsoluteUrl = (url: string) => (!!url && url.length >= 7 && (url[4] === ':' || (url[5] === ':' && url[4] === 's')) && url.startsWith('http'));
const getS3Cmd = (key: string, bucket: string, blogsLocation?: string) => {
  return new GetObjectCommand({
    Bucket: bucket,
    Key: `${blogsLocation ?? ''}${key}`
  });
};

// Cache for signed URLs with expiration
interface CachedUrl {
  url: string;
  expiresAt: number;
}

const urlCache = new Map<string, CachedUrl>();
const DEFAULT_EXPIRATION_SECONDS = 3600; // 1 hour default


export const getSignedItemPath = async (
  itemPath: string, 
  bucket: string, 
  client: S3Client, 
  blogsLocation?: string,
  expiresIn: number = DEFAULT_EXPIRATION_SECONDS
) => {
  const absoluteUrl = isAbsoluteUrl(itemPath);
  
  // Return absolute URLs directly
  if (absoluteUrl || !client) {
    return Promise.resolve(itemPath);
  }
  
  // Create cache key
  const cacheKey = `${bucket}:${blogsLocation ?? ''}${itemPath}`;
  
  // Check cache
  const cached = urlCache.get(cacheKey);
  const now = Date.now();
  
  if (cached && cached.expiresAt > now) {
    return cached.url;
  }
  
  // Generate new signed URL
  const signedUrl = await getSignedUrl(
    client, 
    getS3Cmd(itemPath, bucket, blogsLocation),
    { expiresIn }
  );
  
  // Cache with expiration (subtract 60 seconds as buffer to avoid using expired URLs)
  const expiresAt = now + (expiresIn - 60) * 1000;
  urlCache.set(cacheKey, { url: signedUrl, expiresAt });
  
  return signedUrl;
}
  
export function useSignedUrl(bucket?: string, blogsLocation?: string) {
  const client = useMemo(() => createS3Client(), []);
    
  const getItemSignedUrl = useCallback(async (itemPath: string, expiresIn?: number) => {
    return !!itemPath && !!bucket && !!client
      ? getSignedItemPath(itemPath, bucket, client, blogsLocation, expiresIn)
      : Promise.resolve(undefined);
    },
    [client, bucket, blogsLocation]);

  return getItemSignedUrl;
}